import {createAsyncThunk} from '@reduxjs/toolkit'

import {
    getSaleHistoryByApi,
    getSaleHistoryFilterByApi,
    getSaleHistorySearchByApi,
    SaleHistoryNextResponse,
    SaleHistoryResponse,
} from '@web/actions/saleHistory'
import {isApiAsyncThunkServerPayload} from '@web/helpers/api'
import {infinitePaginationInfoSelector} from '@web/slices/pagination/slice'

import {ThunkApi} from '../interfaces'
import {
    saleHistoryCurrentSearchSelector,
    saleHistoryPageQueryInfoSelector,
    saleHistoryPageRenderSelector,
} from '../selectors'

const actionType = 'saleHistory'

/** 판매탭 초기화 action */
export const initSaleHistory = createAsyncThunk<
    {data: SaleHistoryResponse | SaleHistoryNextResponse},
    void,
    ThunkApi<{
        code: string
        message: string
        replaceUrl?: string
    }>
>(`${actionType}/init`, async (_, {dispatch, getState, rejectWithValue, signal}) => {
    const state = getState()
    const {valid} = saleHistoryPageQueryInfoSelector(state)
    const currentSearch = saleHistoryCurrentSearchSelector(state)
    const {shouldSearch, shouldFilter} = saleHistoryPageRenderSelector(state)

    if (valid.isError) {
        return rejectWithValue({
            code: 'QUERY_FAIL',
            message: '검색필터 체크 필요',
            replaceUrl: valid.replaceUrl,
        })
    }

    try {
        const action = shouldSearch
            ? dispatch(
                  getSaleHistorySearchByApi({
                      search: currentSearch.search,
                      keyword: currentSearch.keyword,
                      category: currentSearch.category,
                      startDate: currentSearch.startDate,
                      endDate: currentSearch.endDate,
                  }),
              )
            : dispatch(
                  getSaleHistoryByApi({
                      page: currentSearch.page,
                  }),
              )

        const filterAction = shouldFilter ? dispatch(getSaleHistoryFilterByApi()) : null

        signal.addEventListener('abort', () => {
            filterAction?.abort()
            action.abort()
        })

        const data = await action.unwrap()

        return {data}
    } catch (e) {
        if (isApiAsyncThunkServerPayload<{code: string; fallbackMessage: string}>(e)) {
            return rejectWithValue({
                code: e.value.response.result.code,
                message: e.value.response.result.fallbackMessage,
            })
        }
        throw e
    }
})

/** 판매탭 다음 검색 action */
export const searchNextSaleHistory = createAsyncThunk<
    {data: SaleHistoryNextResponse},
    void,
    ThunkApi<{
        code: string
        message: string
        replaceUrl?: string
    }>
>(`${actionType}/search`, async (_, {dispatch, getState, rejectWithValue, signal}) => {
    const state = getState()
    const {valid} = saleHistoryPageQueryInfoSelector(state)
    const currentSearch = saleHistoryCurrentSearchSelector(state)
    const {nextCursor} = infinitePaginationInfoSelector(state)

    if (valid.isError) {
        return rejectWithValue({
            code: 'QUERY_FAIL',
            message: '검색필터 체크 필요',
            replaceUrl: valid.replaceUrl,
        })
    }

    try {
        const action = dispatch(
            getSaleHistorySearchByApi({
                search: currentSearch.search,
                keyword: currentSearch.keyword,
                category: currentSearch.category,
                startDate: currentSearch.startDate,
                endDate: currentSearch.endDate,
                nextCursor,
            }),
        )
        signal.addEventListener('abort', () => {
            action.abort()
        })
        const data = await action.unwrap()

        return {data}
    } catch (e) {
        if (isApiAsyncThunkServerPayload<{code: string; fallbackMessage: string}>(e)) {
            return rejectWithValue({
                code: e.value.response.result.code,
                message: e.value.response.result.fallbackMessage,
            })
        }
        throw e
    }
})
